/**
 * Shared deep-link navigation bridge.
 *
 * Push notifications (native iOS via Capacitor, and web via the service worker)
 * all resolve to an in-app URL like `/messages?customerId=...`. Historically each
 * click handler did a HARD navigation (`window.location.href = url` or the SW's
 * `client.navigate()`), which fully reloads the SPA — causing blank screens on the
 * native webview and leaving the app in a fresh-boot state that differs from normal
 * in-app navigation (e.g. the dialler/call flow breaking after opening a thread).
 *
 * This module lets non-React code (native push listener, SW message handler) request
 * an IN-APP (React Router) navigation instead. `DeepLinkNavigator` (mounted inside the
 * Router) listens for the event and calls `navigate()`. For cold starts where the app
 * isn't mounted yet, the target is stashed and replayed once the navigator mounts.
 */

const PENDING_KEY = 'pendingDeepLinkUrl';

/** Event dispatched on `window` to ask the in-app navigator to route to a URL. */
export const DEEP_LINK_EVENT = 'app:navigate';

/** True for app-internal paths we are willing to route to (avoids open-redirects). */
export function isInternalDeepLink(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith('/');
}

/** Persist a pending deep link so a not-yet-mounted Router can replay it after boot. */
export function setPendingDeepLink(url: string): void {
  if (!isInternalDeepLink(url)) return;
  try {
    sessionStorage.setItem(PENDING_KEY, url);
  } catch {
    // sessionStorage may be unavailable (private mode / SW context) — non-fatal.
  }
}

/** Read and clear any pending deep link stashed before the navigator mounted. */
export function consumePendingDeepLink(): string | null {
  try {
    const url = sessionStorage.getItem(PENDING_KEY);
    if (url) sessionStorage.removeItem(PENDING_KEY);
    return isInternalDeepLink(url) ? url : null;
  } catch {
    return null;
  }
}

/**
 * Request an in-app navigation to `url`. Use this from push-notification click
 * handlers instead of `window.location.href`. Stashes the target for cold-start
 * replay and notifies the mounted `DeepLinkNavigator` to route immediately.
 */
export function deepLinkNavigate(url: string): void {
  if (!isInternalDeepLink(url)) return;
  setPendingDeepLink(url);
  try {
    window.dispatchEvent(new CustomEvent(DEEP_LINK_EVENT, { detail: { url } }));
  } catch {
    // If we cannot dispatch (no window), the stashed value will be replayed on mount.
  }
}
